import xss, { escapeAttrValue, escapeHtmlEntities, unescapeQuote } from 'xss';

/**
 * Sanitiser for author-written HTML on the CMS **render** path.
 *
 * Why this exists at all: CMS content is not covered by the API's `@sanitizeHTML` directive. That
 * directive sanitises string fields as they resolve, and 33 fields use it (including the legacy
 * `menuBannerHtml`), but `cmsResolveContent` proxies an opaque nested-JSON page payload from the
 * Web Studio worker. A string-level directive cannot reach a prop buried inside that JSON, so
 * author HTML arrives at the storefront exactly as stored.
 *
 * Why it must run on render rather than at author time: saved page data is hand-editable JSON, and
 * this project already relies on that being true elsewhere. Anything sanitised only in the editor
 * is bypassed by a value that never went through the editor, or that predates the control. The
 * published renderer also has no error boundary, so it is the last place a bad value can be caught.
 *
 * `xss` is the library the API already uses for exactly this job (`apps/api/src/directives/
 * html-sanitize-directive`), pinned to the same 1.0.13, and the allowlist below starts from that
 * directive's list so the CMS and the API agree about what author HTML may contain. Reusing it
 * rather than hand-rolling an allowlist is deliberate: an allowlist assembled by hand is a standing
 * invitation to miss a sink.
 */

/**
 * Tags the API directive already permits for author HTML. Kept identical so the two paths do not
 * disagree about the same content.
 */
const API_ALLOWED_TAGS: Record<string, string[]> = {
  a: ['href'],
  b: [],
  br: [],
  em: [],
  i: [],
  li: [],
  ol: [],
  p: [],
  strong: [],
  ul: [],
};

/**
 * Tags added for Puck's richtext toolbar, each an explicit decision rather than a broader list
 * copied in wholesale:
 *
 * - `u`, `s` — underline and strikethrough buttons. Inline styling only, no attributes.
 * - `h1`-`h6` — semantic output for the Heading component. No attributes beyond native alignment:
 *   an `id` would let an author collide with the storefront's own anchors.
 * - `blockquote` — quote button.
 * - `code`, `pre` — not offered by the toolbar, but Tiptap can emit them from pasted content, and
 *   they are inert.
 *
 * `style` is otherwise deliberately absent (it is an exfiltration and clickjacking surface via
 * `background-image: url(...)` and `position: fixed`). The one exception is Tiptap's text alignment
 * on paragraph/heading tags, validated and normalized by `safeTextAlignStyle` below. Also excluded:
 * `class` (lets author content borrow or break storefront styles), `id`, `img`, `iframe`, `svg`,
 * `use`, `form`, `input`, and `table`.
 */
const RICHTEXT_ALLOWED_TAGS: Record<string, string[]> = {
  blockquote: [],
  code: [],
  h1: ['style'],
  h2: ['style'],
  h3: ['style'],
  h4: ['style'],
  h5: ['style'],
  h6: ['style'],
  p: ['style'],
  pre: [],
  s: [],
  u: [],
};

/**
 * URL schemes permitted on `href`. Everything else is dropped rather than rewritten, so a
 * `javascript:` or `data:` link becomes a plain non-navigating anchor instead of a live one.
 *
 * `data:` is excluded even though `image.tsx` legitimately allows it for image sources: a
 * `data:text/html` href is a same-origin script execution vector, which an image `src` is not.
 */
const ALLOWED_HREF_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'];
const TEXT_ALIGN_STYLE_PATTERN = /^text-align\s*:\s*(left|center|right|justify)\s*;?\s*$/i;

/** Return a normalized Tiptap alignment declaration, or undefined for every other style. */
function safeTextAlignStyle(value: string): string | undefined {
  const match = TEXT_ALIGN_STYLE_PATTERN.exec(value);
  return match ? `text-align: ${match[1].toLowerCase()}` : undefined;
}

/** Protocol-relative and root-relative links stay in-site, so they need no scheme check. */
function isSafeHref(value: string): boolean {
  // XSS calls custom attribute hooks before its default entity decoding. Validate the same decoded
  // value a browser navigates so numeric/HTML5 entities cannot disguise a script scheme.
  const namedControlsDecoded = unescapeQuote(value)
    .replace(/&colon;?/gi, ':')
    .replace(/&newline;?/gi, '\n')
    .replace(/&tab;?/gi, '\t');
  const trimmed = escapeHtmlEntities(namedControlsDecoded).trim();
  if (trimmed === '') {
    return false;
  }
  // A leading control character or whitespace inside the scheme is the classic
  // `java\tscript:` bypass; the URL parser below normalises it, which is why parsing beats a regex.
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return true;
  }
  if (trimmed.startsWith('#')) {
    return true;
  }
  try {
    // A base is required for relative inputs; the resulting origin is irrelevant here because only
    // the protocol is inspected.
    const parsed = new URL(trimmed, 'https://placeholder.invalid');
    return ALLOWED_HREF_SCHEMES.includes(parsed.protocol);
  } catch {
    return false;
  }
}

const xssOptions: XSS.IFilterXSSOptions = {
  whiteList: { ...API_ALLOWED_TAGS, ...RICHTEXT_ALLOWED_TAGS },
  // Drop disallowed tags AND their contents for tags whose body is code rather than prose. Without
  // this, `<script>alert(1)</script>` has its tags stripped and leaves `alert(1)` as visible text.
  stripIgnoreTagBody: ['script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template'],
  // Remove any other disallowed tag but keep its text, so unexpected markup degrades to prose
  // rather than deleting the author's copy.
  stripIgnoreTag: true,
  onTagAttr: (tag, name, value) => {
    if (name === 'style') {
      const alignment = safeTextAlignStyle(value);
      return alignment ? `style="${escapeAttrValue(alignment)}"` : '';
    }
    if (tag === 'a' && name === 'href') {
      if (!isSafeHref(value)) {
        // Returning '' drops the attribute, leaving an inert anchor rather than a live bad link.
        return '';
      }
      // Preserved from the API directive, where it is a security property rather than cosmetic:
      // `noopener` denies the opened page access to `window.opener`, and `noreferrer` withholds the
      // referrer. Author content routinely links off-site.
      return `href="${escapeAttrValue(value)}" target="_blank" rel="noopener noreferrer"`;
    }
    return undefined;
  },
};

/**
 * Returns author HTML with everything outside the allowlist removed.
 *
 * Non-string input returns `''`: the declared prop types say these are strings, but the value that
 * actually arrives is whatever the saved JSON holds.
 */
export function sanitizeCmsHtml(html: unknown): string {
  if (typeof html !== 'string' || html === '') {
    return '';
  }
  return dropTruncatedCodeTagBodies(xss(html, xssOptions));
}

/**
 * Removes what `stripIgnoreTagBody` leaves behind when a code-bearing tag is never closed.
 *
 * `stripIgnoreTagBody` needs a closing tag to know where the body ends, so `<p>Hi</p><script>alert(1)`
 * sanitises to `<p>Hi</p>[removed]alert(1)` -- the script cannot execute, but the code survives as
 * visible copy on the page, which is both ugly and a plausible way to smuggle instructions to a
 * reader. `xss` marks each stripped tag with the literal `[removed]`, so anything following the
 * final marker with no closing tag to bound it is dropped.
 *
 * Runs after `xss`, never instead of it: this trims residue, it is not a security boundary.
 */
function dropTruncatedCodeTagBodies(sanitized: string): string {
  const marker = '[removed]';
  const lastMarker = sanitized.lastIndexOf(marker);
  if (lastMarker === -1) {
    return sanitized;
  }
  const trailing = sanitized.slice(lastMarker + marker.length);
  // A closing tag after the marker means the body was bounded and already handled.
  if (trailing.includes('</') || trailing.includes(marker)) {
    return sanitized;
  }
  return sanitized.slice(0, lastMarker);
}
